import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const DISCOVERY_CALL_URL = 'https://calendly.com/nicolas-gate33/discovery-call';

const NAV_LINKS = [
  { name: 'Home', path: '/' },
  { name: '3-Day Online Training', path: '/academy' },
  { name: 'Mentoring', path: '/mentorship' },
  { name: 'Insights', path: '/insights' },
  { name: 'About', path: '/about' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'backdrop-blur-xl bg-[#FDFBF7]/90 border-b border-[#1A1A1A]/5 shadow-sm'
          : 'bg-[#FDFBF7]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-[auto_1fr_auto] items-center h-20 gap-8">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <span className="font-heading text-2xl font-semibold tracking-wider">
              <span className="text-[#1A1A1A]">GATE</span>
              <span className="text-[#C5A880]">33</span>
            </span>
          </Link>

          {/* Desktop Navigation (centered) */}
          <div className="hidden md:flex items-center justify-center space-x-10">
            {NAV_LINKS.map((link) => {
              const linkActive = isActive(link.path);
              const testId = `nav-link-${link.name.toLowerCase().replace(/ /g, '-')}`;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={testId}
                  className={`text-sm tracking-wide transition-colors relative ${
                    linkActive ? 'text-[#C5A880]' : 'text-[#1A1A1A] hover:text-[#C5A880]'
                  }`}
                >
                  <span className="relative inline-flex items-center gap-1">{link.name}</span>
                  {linkActive && (
                    <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C5A880]" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* CTA Button — Book a Discovery Call */}
          <a
            href={DISCOVERY_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-discovery-call"
            className="hidden md:inline-block bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#1A1A1A] transition-all duration-300"
          >
            Book a Discovery Call
          </a>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-[#1A1A1A]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden py-6 border-t border-[#E5E0D8] animate-fade-in"
          >
            <div className="flex flex-col space-y-4">
              {NAV_LINKS.map((link) => {
                const testId = `mobile-nav-link-${link.name.toLowerCase().replace(/ /g, '-')}`;
                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    data-testid={testId}
                    className={`text-base py-2 transition-colors ${
                      isActive(link.path)
                        ? 'text-[#C5A880]'
                        : 'text-[#1A1A1A] hover:text-[#C5A880]'
                    }`}
                  >
                    <span className="relative inline-block">{link.name}</span>
                  </Link>
                );
              })}
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-cta-discovery-call"
                className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold text-center mt-4 hover:bg-[#C5A880] hover:text-[#1A1A1A] transition-all duration-300"
              >
                Book a Discovery Call
              </a>
              <Link
                to="/academy"
                data-testid="mobile-nav-see-training"
                className="text-xs uppercase tracking-[0.15em] text-[#5A5A5A] text-center mt-2 hover:text-[#C5A880] transition-colors"
              >
                or see the training →
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
